import React, { useState } from 'react';
import { useEffect } from 'react';
import { Search, Filter, Calendar, CheckCircle, X, Clock, Phone, Mail, MessageCircle, Linkedin, FileText, Play, Globe, User, Building, Smartphone, Tag, Users, AlertCircle } from 'lucide-react';
import { format, isThisWeek, parseISO } from 'date-fns';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import MeetingDetailsModal from './MeetingDetailsModal';

const MeetingsPage: React.FC = () => {
  const { data: allMeetings, isLoading, error, isAuthenticated } = useGoogleSheets('meetings');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [showMeetingDetailsModal, setShowMeetingDetailsModal] = useState(false);
  const itemsPerPage = 10;

  const filteredMeetings = allMeetings.filter(meeting => {
    const matchesSearch = (meeting.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (meeting.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (meeting.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || meeting.meeting_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMeetings = filteredMeetings.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Calculate statistics
  const totalMeetings = allMeetings.length;
  const attendedMeetings = allMeetings.filter(meeting => meeting.meeting_status === 'attended').length;
  const missedMeetings = allMeetings.filter(meeting => meeting.meeting_status === 'missed').length;
  const meetingsThisWeek = allMeetings.filter(meeting => {
    if (!meeting.meeting_date) return false;
    try {
      return isThisWeek(parseISO(meeting.meeting_date));
    } catch {
      return false;
    }
  }).length;

  const getMeetingStatusTag = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'attended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <CheckCircle size={12} className="mr-1" />
            Attended
          </span>
        );
      case 'missed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <X size={12} className="mr-1" />
            Missed
          </span>
        );
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Clock size={12} className="mr-1" />
            Upcoming
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <X size={12} className="mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const getAssignedToTag = (salesPerson: string) => {
    if (!salesPerson) return <span className="text-gray-400 text-sm">Unassigned</span>;
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
        <User size={12} className="mr-1" />
        {salesPerson}
      </span>
    );
  };

  const getChannelTag = (channel: string) => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <MessageCircle size={12} className="mr-1" />
            WhatsApp
          </span>
        );
      case 'email':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
            <Mail size={12} className="mr-1" />
            Email
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Linkedin size={12} className="mr-1" />
            LinkedIn
          </span>
        );
      case 'call':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
            <Phone size={12} className="mr-1" />
            Call
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {channel || 'Unknown'}
          </span>
        );
    }
  };

  const getPipelineStageTag = (stage: string) => {
    switch (stage?.toLowerCase()) {
      case 'proposal sent':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
            Proposal Sent
          </span>
        );
      case 'meeting booked':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
            Meeting Booked
          </span>
        );
      case 'interested':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
            Interested
          </span>
        );
      case 'qualified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            Qualified
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {stage || 'Unknown'}
          </span>
        );
    }
  };

  const formatDateTime = (date: string, time: string) => {
    if (!date) return 'N/A';
    try {
      const dateObj = parseISO(date);
      const formattedDate = format(dateObj, 'MMM dd, yyyy');
      const formattedTime = time || 'N/A';
      return { date: formattedDate, time: formattedTime };
    } catch {
      return { date: date, time: time || 'N/A' };
    }
  };

  const handleRowClick = (meeting: any) => {
    setSelectedMeeting(meeting);
    setShowMeetingDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowMeetingDetailsModal(false);
    setSelectedMeeting(null);
  };

  // Only show loading if we're loading AND have no data to display
  if (isLoading && allMeetings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading data...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Meetings Booked</p>
              <p className="text-3xl font-bold text-blue-900">{totalMeetings}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Attended</p>
              <p className="text-3xl font-bold text-green-900">{attendedMeetings}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-red-50 rounded-xl border border-red-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">Missed</p>
              <p className="text-3xl font-bold text-red-900">{missedMeetings}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="text-red-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600 mb-1">Meetings This Week</p>
              <p className="text-3xl font-bold text-purple-900">{meetingsThisWeek}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search companies or contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Meetings</option>
                <option value="upcoming">Upcoming</option>
                <option value="attended">Attended</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Meetings Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Meeting ID
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Company
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Decision Maker
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Date & Time
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Assigned To
                </th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Status
                </th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Transcript & Recording
                </th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Booked Channel
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedMeetings.map((meeting, index) => {
                const { date, time } = formatDateTime(meeting.meeting_date, meeting.meeting_time);
                return (
                  <tr 
                    key={meeting._rowIndex || meeting.meeting_id || index} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleRowClick(meeting)}
                  >
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {meeting.meeting_id || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="font-medium text-gray-900 mb-2">
                          {meeting.company_name || 'Unknown Company'}
                        </div>
                        <div className="flex items-center space-x-2">
                          {meeting.company_website && (
                            <a 
                              href={meeting.company_website.startsWith('http') ? meeting.company_website : `https://${meeting.company_website}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Visit Website"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe size={16} />
                            </a>
                          )}
                          {meeting.company_linkedin && (
                            <a 
                              href={meeting.company_linkedin.startsWith('http') ? meeting.company_linkedin : `https://${meeting.company_linkedin}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="LinkedIn Profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {meeting.dm_full_name || 'Unknown'}
                          </span>
                          {meeting.dm_linkedin && (
                            <a 
                              href={meeting.dm_linkedin.startsWith('http') ? meeting.dm_linkedin : `https://${meeting.dm_linkedin}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="LinkedIn Profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin size={14} />
                            </a>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {meeting.dm_title || 'Unknown Title'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {date}
                        </div>
                        <div className="text-sm text-gray-500">
                          {time}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getAssignedToTag(meeting.sales_person)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getMeetingStatusTag(meeting.meeting_status)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        {meeting.transcript ? (
                          <a
                            href={meeting.transcript}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FileText size={14} />
                            <span>Transcript</span>
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">No transcript</span>
                        )}
                        {meeting.recording ? (
                          <a
                            href={meeting.recording}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-green-600 hover:text-green-800 font-medium text-sm transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Play size={14} />
                            <span>Recording</span>
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">No recording</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getChannelTag(meeting.booked_channel)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredMeetings.length)} of {filteredMeetings.length} results
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {paginatedMeetings.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No meetings found</div>
          <div className="text-gray-400 text-sm mt-1">
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Meetings will appear here once they are booked'}
          </div>
        </div>
      )}

      {/* Meeting Details Modal */}
      {showMeetingDetailsModal && selectedMeeting && (
        <MeetingDetailsModal
          meeting={selectedMeeting}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MeetingsPage;